import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const WhoItsForSection = () => {
  const navigate = useNavigate();

  const audiences = [
    {
      title: 'Patients & Caregivers',
      subtitle: 'Take control of your health journey',
      icon: 'Heart',
      color: 'primary',
      painPoints: [
        {
          icon: 'AlertCircle',
          title: 'Difficulty Remembering Symptoms',
          description: 'Struggle to recall specific symptoms, their severity, and timing during medical appointments'
        },
        {
          icon: 'MessageSquare',
          title: 'Feeling Unheard During Appointments',
          description: 'Limited time with providers makes it hard to communicate the full picture of your health'
        },
        {
          icon: 'Clock',
          title: 'Inconsistent Health Tracking',
          description: 'Traditional methods are time-consuming and difficult to maintain consistently'
        }
      ],
      benefits: [
        'Become an active participant in your healthcare decisions',
        'Clear, organized health data to share with your medical team',
        'Better communication and understanding with your providers',
        'Personalized insights to help manage your condition'
      ],
      cta: {
        text: 'Start as Patient',
        action: () => navigate('/patient-dashboard')
      }
    },
    {
      title: 'Healthcare Providers',
      subtitle: 'Make informed decisions with complete data',
      icon: 'Stethoscope',
      color: 'accent',
      painPoints: [
        {
          icon: 'Clock',
          title: 'Limited Time with Patients',
          description: 'Short appointment windows make it challenging to gather comprehensive health information'
        },
        {
          icon: 'Database',
          title: 'Incomplete Patient Data',
          description: 'Relying on patient recall and fragmented information from different sources'
        },
        {
          icon: 'TrendingUp',
          title: 'Difficulty Identifying Patterns',
          description: 'Manual analysis of symptoms and trends is time-intensive and prone to oversight'
        }
      ],
      benefits: [
        'Access comprehensive patient data between visits',
        'AI-powered analysis reveals hidden patterns and trends',
        'Spend more quality time on patient care, less on data gathering',
        'Make more informed treatment decisions with complete information'
      ],
      cta: {
        text: 'Start as Provider',
        action: () => navigate('/provider-dashboard')
      }
    }
  ];

  const PainPointCard = ({ painPoint, color }) => (
    <div className="flex items-start space-x-3 p-4 bg-card border border-border rounded-lg hover:border-destructive/20 transition-colors">
      <div className="flex-shrink-0 w-8 h-8 bg-destructive/10 rounded-lg flex items-center justify-center mt-1">
        <Icon name={painPoint?.icon} size={16} className="text-destructive" />
      </div>
      <div>
        <h4 className="font-medium text-card-foreground mb-1">{painPoint?.title}</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">{painPoint?.description}</p>
      </div>
    </div>
  );

  const BenefitItem = ({ benefit, color }) => (
    <div className="flex items-start space-x-3">
      <div className={`flex-shrink-0 w-5 h-5 bg-${color}/10 rounded-full flex items-center justify-center mt-0.5`}>
        <Icon name="Check" size={12} className={`text-${color}`} />
      </div>
      <p className="text-muted-foreground">{benefit}</p>
    </div>
  );

  return (
    <section id="who-its-for" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Who It's For
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              MycoMind is designed for two key audiences who are essential partners 
              in neurological healthcare - each with unique needs and challenges.
            </p>
          </div>

          {/* Audiences */}
          <div className="space-y-16">
            {audiences?.map((audience, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className={`w-12 h-12 bg-${audience?.color}/10 rounded-xl flex items-center justify-center mr-4`}>
                      <Icon name={audience?.icon} size={24} className={`text-${audience?.color}`} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">{audience?.title}</h3>
                      <p className="text-muted-foreground">{audience?.subtitle}</p>
                    </div>
                  </div>

                  {/* Pain Points */}
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                      <Icon name="AlertTriangle" size={18} className="text-destructive mr-2" />
                      Common Challenges
                    </h4>
                    <div className="space-y-3">
                      {audience?.painPoints?.map((painPoint, idx) => (
                        <PainPointCard key={idx} painPoint={painPoint} color={audience?.color} />
                      ))}
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                      <Icon name="Sparkles" size={18} className={`text-${audience?.color} mr-2`} />
                      How MycoMind Helps
                    </h4>
                    <div className="space-y-3">
                      {audience?.benefits?.map((benefit, idx) => (
                        <BenefitItem key={idx} benefit={benefit} color={audience?.color} />
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <Button
                    size="lg"
                    variant={audience?.color === 'primary' ? 'default' : 'outline'}
                    onClick={audience?.cta?.action}
                    className="w-full sm:w-auto"
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    {audience?.cta?.text}
                  </Button>
                </div>

                {/* Visual */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className={`bg-gradient-to-br from-${audience?.color}/10 to-${audience?.color}/5 rounded-2xl p-8 h-80 flex items-center justify-center relative overflow-hidden`}>
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-grid-slate-900/[0.04] bg-[size:30px_30px]" />
                    
                    {/* Central Icon */}
                    <div className={`w-24 h-24 bg-${audience?.color}/20 rounded-2xl flex items-center justify-center relative z-10`}>
                      <Icon name={audience?.icon} size={48} className={`text-${audience?.color}`} />
                    </div>

                    {/* Floating Elements */}
                    <div className="absolute top-6 right-6 w-12 h-12 bg-card border border-border rounded-lg flex items-center justify-center shadow-sm">
                      <Icon name="Plus" size={16} className="text-muted-foreground" />
                    </div>
                    <div className="absolute bottom-6 left-6 w-16 h-8 bg-card border border-border rounded-lg flex items-center justify-center shadow-sm">
                      <div className="flex space-x-1">
                        <div className={`w-2 h-2 bg-${audience?.color} rounded-full`} />
                        <div className={`w-2 h-2 bg-${audience?.color}/50 rounded-full`} />
                        <div className={`w-2 h-2 bg-${audience?.color}/25 rounded-full`} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoItsForSection;